import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import { branding, documentMimeTypes, imageMimeTypes, videoMimeTypes } from '../../../config/enum';
import { getTimeZones, getUserTimeZone, normalizeTimeZone } from '../../../config/timezonesLabel';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../../interface/file-upload-stream.interface';
import { StationDetails, Amenity } from '../../../interface/station-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../redux/slice/FileUploadStreamSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomAddressInput from '../../../shared-components/custom-address-input/CustomAddressInput';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../../../shared-components/custom-media-upload/CustomMediaUpload';
import CustomTimePicker from '../../../shared-components/custom-time-input/CustomTimePicker';
import FileUploadProgress from '../../../shared-components/file-upload-progress/FileUploadProgress';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
// import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SelectLocation from '../../../shared-components/select-location/SelectLocation';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import TabToggle from '../../../shared-components/tab-toggle/TabToggle';
import { SanitizeFileName } from '../../../shared-functions/StringFunctions';
import {
  checkFileSizeValidation,
  longTextValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import station from '../../../utils/api/station';

import AmenitiesInput from './AmenitiesInput';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add new station component
 */
interface AddStationProperties {
  onClose?: () => void;
  onSuccess?: () => void;
}

interface DayOperatingHours {
  isOpen: boolean;
  from: string;
  to: string;
}
interface OperatingHours {
  allDay: boolean;
  monday: DayOperatingHours;
  tuesday: DayOperatingHours;
  wednesday: DayOperatingHours;
  thursday: DayOperatingHours;
  friday: DayOperatingHours;
  saturday: DayOperatingHours;
  sunday: DayOperatingHours;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/**
 * Add new station component
 */
function AddStation({ onClose, onSuccess }: AddStationProperties) {
  const { t } = useTranslation('stationManagement');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [stationDetails, setStationDetails] = useState<StationDetails>({
    name: '',
    landOwnerShipDocument: [],
    ebReport: [],
    electricityContractType: '',
    timeZone: getUserTimeZone(),
    operatingHours: {
      allDay: true,
      monday: { isOpen: true, from: '00:00', to: '23:59' },
      tuesday: { isOpen: true, from: '00:00', to: '23:59' },
      wednesday: { isOpen: true, from: '00:00', to: '23:59' },
      thursday: { isOpen: true, from: '00:00', to: '23:59' },
      friday: { isOpen: true, from: '00:00', to: '23:59' },
      saturday: { isOpen: true, from: '00:00', to: '23:59' },
      sunday: { isOpen: true, from: '00:00', to: '23:59' },
    },
    address: '',
    latitude: null,
    longitude: null,
    medias: [],
    amenities: [],
    electricityCostByUtilityProvider: null,
    isGuestChargingExclusive: false,
  });
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const listOfCountryCode = [
    {
      label: t('standardResidential'),
      value: 'standard-residential',
    },
    {
      label: t('commercial'),
      value: 'commercial',
    },
    {
      label: t('industrial'),
      value: 'industrial',
    },
    {
      label: t('evChargingSpecificContracts'),
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle input value change
   * @param name
   * @param changeValue
   */
  const handleStationDetailChange = (
    name: string,
    changeValue: string | number | File[] | Amenity[] | boolean,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: changeValue,
    });
  };

  /**
   * Handle Change address
   */
  const handleChangeAddress = (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: address,
      latitude,
      longitude,
    });
  };

  /**
   * Handle lat & long change
   * @param changeValue
   */
  const handleChangeLatLong = (latitude: number, longitude: number) => {
    setStationDetails((previousValue) => ({
      ...previousValue,
      latitude,
      longitude,
    }));
  };

  /**
   * Handle operating hours change
   * @param day
   * @param field
   * @param value
   */
  const handleOperatingHoursChange = (day: DayKey, field: 'isOpen' | 'from' | 'to', value: any) => {
    setStationDetails((previous) => ({
      ...previous,
      operatingHours: {
        ...previous.operatingHours,
        [day]: {
          ...previous.operatingHours[day],
          [field]: value,
        },
      },
    }));
  };
  /**
   * Handle All Day toggle
   * @param name
   * @param value
   */
  const handleAllDayToggle = (name: string, value: boolean) => {
    setStationDetails((previous) => {
      const updatedOperatingHours = { ...previous.operatingHours! };
      updatedOperatingHours.allDay = value;

      if (value) {
        const fullDay = { isOpen: true, from: '00:00', to: '23:59' };
        updatedOperatingHours.monday = fullDay;
        updatedOperatingHours.tuesday = fullDay;
        updatedOperatingHours.wednesday = fullDay;
        updatedOperatingHours.thursday = fullDay;
        updatedOperatingHours.friday = fullDay;
        updatedOperatingHours.saturday = fullDay;
        updatedOperatingHours.sunday = fullDay;
      } else {
        const closedDay = { isOpen: false, from: '00:00', to: '23:59' };
        updatedOperatingHours.monday = closedDay;
        updatedOperatingHours.tuesday = closedDay;
        updatedOperatingHours.wednesday = closedDay;
        updatedOperatingHours.thursday = closedDay;
        updatedOperatingHours.friday = closedDay;
        updatedOperatingHours.saturday = closedDay;
        updatedOperatingHours.sunday = closedDay;
      }

      return {
        ...previous,
        operatingHours: updatedOperatingHours,
      };
    });
  };

  /**
   * Handle Day Toggle
   * @param day
   * @param isOpen
   */
  const handleDayToggle = (day: DayKey, isOpen: boolean) => {
    handleOperatingHoursChange(day, 'isOpen', isOpen);
  };

  /**
   * Function to get and handle file upload progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setStationDetails({
      name: '',
      landOwnerShipDocument: [],
      ebReport: [],
      electricityContractType: '',
      timeZone: getUserTimeZone(),
      operatingHours: {
        allDay: false,
        monday: { isOpen: true, from: '00:00', to: '23:59' },
        tuesday: { isOpen: true, from: '00:00', to: '23:59' },
        wednesday: { isOpen: true, from: '00:00', to: '23:59' },
        thursday: { isOpen: true, from: '00:00', to: '23:59' },
        friday: { isOpen: true, from: '00:00', to: '23:59' },
        saturday: { isOpen: true, from: '00:00', to: '23:59' },
        sunday: { isOpen: false, from: '00:00', to: '23:59' },
      },
      address: '',
      latitude: null,
      longitude: null,
      medias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
    });
    setModalBoxStatus('close');

    if (onClose) {
      onClose();
    } else {
      navigate('../approved');
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('title')}
          description={t('description')}
          cancelButtonLabel={t('cancelButton')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('okButton')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Add station API call
   */
  const handleSubmitStationDetails = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (
      stringValidation(1, 100, stationDetails.name) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.landOwnerShipDocument &&
          stationDetails.landOwnerShipDocument.length > 0 &&
          checkFileSizeValidation(stationDetails.landOwnerShipDocument, 5, 1, 0, [
            ...imageMimeTypes,
            ...documentMimeTypes,
          ]))) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.ebReport &&
          stationDetails.ebReport.length > 0 &&
          checkFileSizeValidation(stationDetails.ebReport, 5, 1, 0, documentMimeTypes))) &&
      stationDetails.electricityContractType &&
      longTextValidation(500, stationDetails.address) &&
      stationDetails.latitude &&
      stationDetails.longitude &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.medias &&
          stationDetails.medias.length > 0 &&
          checkFileSizeValidation(stationDetails.medias, 10, 10, 100, [
            ...imageMimeTypes,
            ...videoMimeTypes,
          ]))) &&
      stationDetails.electricityCostByUtilityProvider &&
      stationDetails.electricityCostByUtilityProvider > 0
      // stationDetails.amenities.length > 0
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', stationDetails.name);
      formData.append('electricityContractType', stationDetails.electricityContractType);
      formData.append('address', stationDetails.address);
      formData.append(
        'electricityCostByUtilityProvider',
        stationDetails.electricityCostByUtilityProvider.toString(),
      );
      formData.append(
        'isGuestChargingExclusive',
        stationDetails.isGuestChargingExclusive.toString(),
      );
      formData.append(
        'latitude',
        stationDetails.latitude ? stationDetails.latitude.toString() : '',
      );

      formData.append(
        'longitude',
        stationDetails.longitude ? stationDetails.longitude.toString() : '',
      );
      formData.append('time_zone', stationDetails.timeZone || '');

      /** operating hours */
      const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ] as const;

      days.forEach((day) => {
        const dayData = stationDetails.operatingHours?.[day];

        if (dayData?.isOpen) {
          formData.append(`operatingHours[${day}][open]`, dayData.from);
          formData.append(`operatingHours[${day}][close]`, dayData.to);
          formData.append(`operatingHours[${day}][status]`, 'open');
        } else {
          formData.append(`operatingHours[${day}][status]`, 'closed');
        }
      });

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(stationDetails.landOwnerShipDocument)) {
        stationDetails.landOwnerShipDocument.forEach((file) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(stationDetails.ebReport)) {
        stationDetails.ebReport.forEach((file) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append multiple files for medias (if it's an array)
      if (stationDetails.medias && Array.isArray(stationDetails.medias)) {
        stationDetails.medias.forEach((media) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (stationDetails.amenities && Array.isArray(stationDetails.amenities)) {
        stationDetails.amenities.forEach((amenity, index) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      const binaryFileData: SingleFileUploadProgress[] = [
        ...stationDetails.landOwnerShipDocument,
        ...stationDetails.medias,
        ...stationDetails.ebReport,
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title:
            binaryFileData?.length === 1
              ? t('uploadingFile')
              : `${binaryFileData?.length} ${t('uploadingFiles')}`,
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      station
        .addStation(formData, handleUploadProgress)
        .then(() => {
          dispatch(setUnUploadedFileData([]));
          onSuccess?.(); // Trigger refresh
          handleNavigateBack(); // Navigate back (close modal/redirect)

          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('stationAddedSuccessfully')}
                description={t('stationSavedSuccessfully')}
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          console.error(error);
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('close');
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message:
                  error?.response?.data?.message || error?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };
  const [openTab, setOpenTab] = useState<number>(0);

  /**
   * Change tab + update URL
   * @param tabLabel
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('stationInformation'), t('operatingHours')].indexOf(tabLabel);
    if (index !== -1) {
      setOpenTab(index);
    }
  };
  const timeZoneOptions = React.useMemo(() => getTimeZones(), []);

  return (
    <div className="add-station-panel">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />

      <p className="panel-header">{t('addStation')}</p>
      <div className="add-station-tabs">
        <TabToggle
          tabs={[t('stationInformation'), t('operatingHours')]}
          defaultTab="stationInformation"
          onChange={(tab) => changeTabPages(tab)}
        />
      </div>
      <div className="panel-form">
        {openTab === 0 && (
          <div className="add-station-from">
            <CustomInput
              inputLabel={t('stationName')}
              type="email"
              placeholder={t('enterName')}
              name="name"
              value={stationDetails.name}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={!stringValidation(1, 100, stationDetails.name) && formStatus.showFormErrors}
              errorMessage={`${t('stationName')} ${t('required')}`}
            />
            <SelectFiled
              inputLabel={t('chooseContractType')}
              name="electricityContractType"
              placeholder={t('chooseContractType')}
              selectedValue={stationDetails.electricityContractType}
              onChangeValue={handleStationDetailChange}
              options={listOfCountryCode}
              valueRequired
              error={!stationDetails.electricityContractType && formStatus.showFormErrors}
              errorMessage={t('electricityContError')}
            />
            <CustomFileInput
              inputLabel={t('ebReportLabel')}
              acceptedFileType="application/pdf"
              placeholder={t('uploadPdf')}
              name="ebReport"
              selectedFiles={stationDetails.ebReport}
              onChangeValue={handleStationDetailChange}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                currentBranding !== branding.VOLTIE &&
                (!stationDetails.ebReport ||
                  stationDetails.ebReport.length === 0 ||
                  !checkFileSizeValidation(stationDetails.ebReport, 5, 1, 0, [
                    ...documentMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage={t('documentRequiredError')}
            />

            <CustomAddressInput
              inputLabel={t('address')}
              placeholder={t('enterAddress')}
              name="address"
              value={stationDetails.address}
              onChangeValue={handleChangeAddress}
              valueRequired
              error={!longTextValidation(500, stationDetails.address) && formStatus.showFormErrors}
              errorMessage={t('addressRequiredError')}
            />
            <SelectLocation
              inputLabel={t('latitudeLongitude')}
              latitude={stationDetails.latitude}
              longitude={stationDetails.longitude}
              onChangeValue={handleChangeLatLong}
              valueRequired
              error={
                !(stationDetails.latitude && stationDetails.longitude) && formStatus.showFormErrors
              }
              errorMessage={t('latLongRequiredError')}
            />
            <CustomMediaUpload
              inputLabel={t('uploadMedia')}
              acceptedFileType=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.mkv"
              placeholder={t('uploadMediaPlaceholder')}
              name="medias"
              selectedFiles={stationDetails.medias}
              valueRequired={currentBranding !== branding.VOLTIE}
              onChangeValue={handleStationDetailChange}
              error={
                currentBranding !== branding.VOLTIE &&
                (stationDetails.medias?.length === 0 ||
                  !checkFileSizeValidation(stationDetails.medias, 10, 10, 100, [
                    ...imageMimeTypes,
                    ...videoMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage={t('documentRequiredError')}
            />
            <CustomInput
              inputLabel={t('usageCost')}
              type="number"
              placeholder={t('enterUsageCost')}
              name="electricityCostByUtilityProvider"
              value={stationDetails.electricityCostByUtilityProvider}
              onChangeValue={handleStationDetailChange}
              valueRequired
              disallowNegative
              error={
                !(
                  stationDetails.electricityCostByUtilityProvider &&
                  stationDetails.electricityCostByUtilityProvider > 0
                ) && formStatus.showFormErrors
              }
              errorMessage={t('usageCostRequiredError')}
            />
            <AmenitiesInput
              selectedAmenities={stationDetails.amenities}
              handleStationDetailChange={handleStationDetailChange}
            />
            <CustomFileInput
              inputLabel={t('landDocument')}
              acceptedFileType="image/*, application/pdf"
              placeholder={t('uploadPdf')}
              name="landOwnerShipDocument"
              selectedFiles={stationDetails.landOwnerShipDocument}
              onChangeValue={handleStationDetailChange}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                currentBranding !== branding.VOLTIE &&
                (!stationDetails.landOwnerShipDocument ||
                  stationDetails.landOwnerShipDocument.length === 0 ||
                  !checkFileSizeValidation(stationDetails.landOwnerShipDocument, 5, 1, 0, [
                    ...imageMimeTypes,
                    ...documentMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage={t('documentRequiredError')}
            />

            <CustomInputSwitch
              inputLabel={t('guestCharging')}
              value={stationDetails.isGuestChargingExclusive}
              name="isGuestChargingExclusive"
              onChangeValue={handleStationDetailChange}
            />
          </div>
        )}
      </div>
      {openTab === 1 && (
        <div className="operating-hours-container">
          <div className="timezone-section">
            <SelectFiled
              inputLabel={t('timeZone')}
              name="timeZone"
              placeholder={t('selectTimeZone')}
              selectedValue={stationDetails.timeZone || ''}
              onChangeValue={(name, value) =>
                setStationDetails((previous) => ({
                  ...previous,
                  [name]: normalizeTimeZone(value as string),
                }))
              }
              options={timeZoneOptions}
              valueRequired
            />
          </div>

          <div className="opening-hours-section">
            <div className="section-header">
              <p className="section-title">{t('stationOpeningHours')}</p>
              <div className="switch-button">
                <p>{t('allDay')}</p>
                <div>
                  <CustomInputSwitch
                    inputLabel=""
                    name="allDay"
                    value={stationDetails.operatingHours?.allDay}
                    onChangeValue={handleAllDayToggle}
                  />{' '}
                </div>
              </div>
            </div>

            <div className="days-config-layout">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(
                (day) => {
                  const dayData = stationDetails.operatingHours?.[
                    day as keyof OperatingHours
                  ] as any;
                  return (
                    <div className="day-row" key={day}>
                      <div className="day-toggle-name">
                        <CustomInputSwitch
                          name={day}
                          value={dayData?.isOpen}
                          onChangeValue={(name, value) => handleDayToggle(name as DayKey, value)}
                        />
                        <p className="day-name">{t(day)}</p>
                      </div>

                      {dayData?.isOpen ? (
                        <div className="day-times-wrap">
                          <div className="day-times">
                            <div className="time-field-wrap">
                              <CustomTimePicker
                                innerLabel={t('from')}
                                name={`${day}-from`}
                                value={dayData?.from}
                                onChangeValue={(_n, v) =>
                                  handleOperatingHoursChange(day as DayKey, 'from', v)
                                }
                              />
                            </div>
                            <div className="time-field-wrap">
                              <CustomTimePicker
                                innerLabel={t('to')}
                                name={`${day}-to`}
                                value={dayData?.to}
                                onChangeValue={(_n, v) =>
                                  handleOperatingHoursChange(day as DayKey, 'to', v)
                                }
                              />
                            </div>
                          </div>
                          {dayData.from &&
                            dayData.to &&
                            dayData.from >= dayData.to &&
                            formStatus.showFormErrors &&
                            !stationDetails.operatingHours?.allDay && (
                              <p className="text-danger m-0 pt-1" style={{ fontSize: '12px' }}>
                                {t('closeTimeGreaterThanOpenTime')}
                              </p>
                            )}
                        </div>
                      ) : (
                        <div className="day-closed">
                          <p className="closed-text">{t('closed')}</p>
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      )}
      <div className="add-station-button-wrap">
        <div className="panel-footer">
          <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmitStationDetails}
            loading={formStatus.buttonLoader}
            disableButton={formStatus.buttonLoader}
          />
        </div>
      </div>
    </div>
  );
}

export default AddStation;

AddStation.defaultProps = {
  /**
   * Callback function to be called when the close button is clicked
   */
  onClose: () => {},
  /**
   * Callback function to be called when the station is added successfully
   */
  onSuccess: () => {},
};
